import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useCartStore, useSettingsStore } from '../../hooks';
import { Button } from '@lancar-pos/ui';

interface CartSummaryProps {
  onCheckout: () => void;
  onClear?: () => void;
  loading?: boolean;
}

export function CartSummary({ onCheckout, onClear, loading }: CartSummaryProps) {
  const { items, getSubtotal, getTax, getTotal, clearCart, getItemCount } = useCartStore();
  const { getSetting } = useSettingsStore();
  const [showClearDialog, setShowClearDialog] = useState(false);

  const taxRate = getSetting<number>('tax_rate') ?? 11;
  const taxEnabled = getSetting<boolean>('tax_enabled') !== false;
  const itemCount = getItemCount();
  const subtotal = getSubtotal();
  const tax = taxEnabled ? getTax(taxRate) : 0;
  const total = getTotal(taxRate, taxEnabled);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleClear = () => {
    clearCart();
    setShowClearDialog(false);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <p className="text-gray-500 font-medium">Keranjang kosong</p>
        <p className="text-sm text-gray-400 mt-1">Pilih produk untuk menambahkan</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-gray-600">
            <span>Subtotal ({itemCount} item)</span>
            <span>{formatPrice(subtotal)}</span>
          </div>

          {taxEnabled && (
            <div className="flex justify-between text-gray-600">
              <span>Pajak ({taxRate}%)</span>
              <span>{formatPrice(tax)}</span>
            </div>
          )}

          <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t border-gray-200">
            <span>Total</span>
            <span>{formatPrice(total)}</span>
          </div>
        </div>

        <div className="space-y-2">
          <button
            onClick={onCheckout}
            disabled={loading}
            className="
              w-full py-3 px-4 rounded-xl font-semibold
              bg-primary-600 text-white
              hover:bg-primary-700 active:bg-primary-800
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2
            "
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Memproses...</span>
              </>
            ) : (
              <>
                <span>Bayar</span>
                <span className="text-primary-200">{formatPrice(total)}</span>
              </>
            )}
          </button>

          {onClear && (
            <button
              onClick={() => setShowClearDialog(true)}
              disabled={loading}
              className="
                w-full py-2 px-4 rounded-xl font-medium
                text-red-600 hover:bg-red-50 active:bg-red-100
                transition-colors duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Hapus Semua
            </button>
          )}
        </div>
      </div>

      {showClearDialog && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowClearDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Kosongkan Keranjang?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Semua item di keranjang akan dihapus. Tindakan ini tidak dapat dibatalkan.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowClearDialog(false)}>
                Batal
              </Button>
              <Button variant="danger" onClick={handleClear}>
                Ya, Hapus
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
